import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import InfoIcon from '../assets/icons/additionalinfo.svg';

function AddNewDevice() {
  const navigate = useNavigate();
  const location = useLocation();
  const user =
    localStorage.getItem('username') ||
    sessionStorage.getItem('username') ||
    'demo';
  const editingDevice = location.state?.device;
  const isEdit = !!editingDevice;

  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [units, setUnits] = useState([]);
  const [health, setHealth] = useState({
    avg: { x: '', y: '', z: '' },
    above: { x: '', y: '', z: '' },
    limit: { x: '', y: '', z: '' },
  });
  const [temps, setTemps] = useState({ avg: '', above: '', limit: '' });
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  useEffect(() => {
    fetch(`/get_units.php?username=${encodeURIComponent(user)}`)
      .then((res) => res.json())
      .then((data) => setUnits(data))
      .catch(() => setUnits([]));
  }, [user]);

  useEffect(() => {
    if (isEdit && editingDevice) {
      fetch(`/get_device.php?username=${encodeURIComponent(user)}&unit=${encodeURIComponent(editingDevice.unit)}`)
        .then((res) => res.json())
        .then((data) => {
          setName(data.device_name || '');
          setUnit(data.unit || '');
          setTemps({
            avg: data.avg_temp || '',
            above: data.above_avg_temp || '',
            limit: data.limit_temp || '',
          });
          setHealth({
            avg: { x: data.avg_x || '', y: data.avg_y || '', z: data.avg_z || '' },
            above: { x: data.warn_x || '', y: data.warn_y || '', z: data.warn_z || '' },
            limit: { x: data.fail_x || '', y: data.fail_y || '', z: data.fail_z || '' },
          });
          setDescription(data.description || '');
          if (data.image_path) setPreview(data.image_path);
        })
        .catch(() => {});
    }
  }, [isEdit, editingDevice, user]);

  const handleHealthChange = (type, axis, value) => {
    setHealth((prev) => ({
      ...prev,
      [type]: { ...prev[type], [axis]: value },
    }));
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImage(null);
    setPreview(null);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove this profile?')) return;
    try {
      const form = new URLSearchParams();
      form.append('username', user);
      form.append('unit', unit);
      const res = await fetch('/delete_device.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: form.toString(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        navigate('/dashboard/overview');
      } else {
        setToast({ message: '❌ Failed to remove device', success: false });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: '❌ Failed to remove device', success: false });
    }
  };

  const clearForm = () => {
    setName('');
    setUnit('');
    setHealth({
      avg: { x: '', y: '', z: '' },
      above: { x: '', y: '', z: '' },
      limit: { x: '', y: '', z: '' },
    });
    setTemps({ avg: '', above: '', limit: '' });
    setDescription('');
    removeImage();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredTemps = Object.values(temps).every((v) => v !== '');
    const requiredHealth = Object.values(health).every((h) =>
      Object.values(h).every((v) => v !== '')
    );
    const missingFields =
      !name ||
      !unit ||
      !description ||
      (!isEdit && !image) ||
      !requiredTemps ||
      !requiredHealth;
    if (missingFields) {
      setToast({ message: 'Please fill in all fields', success: false });
      return;
    }

    const formData = new FormData();
    formData.append('username', user);
    formData.append('device_name', name);
    formData.append('unit', unit);
    formData.append('description', description);
    formData.append('avg_temp', temps.avg);
    formData.append('above_avg_temp', temps.above);
    formData.append('limit_temp', temps.limit);
    formData.append('avg_x', health.avg.x);
    formData.append('avg_y', health.avg.y);
    formData.append('avg_z', health.avg.z);
    formData.append('warn_x', health.above.x);
    formData.append('warn_y', health.above.y);
    formData.append('warn_z', health.above.z);
    formData.append('fail_x', health.limit.x);
    formData.append('fail_y', health.limit.y);
    formData.append('fail_z', health.limit.z);
    if (image) {
      formData.append('image', image);
    }

    try {
      const res = await fetch(isEdit ? '/update_device.php' : '/add_device.php', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (!isEdit) clearForm();
        setToast({ message: `✅ Device ${isEdit ? 'updated' : 'added'}`, success: true });
      } else {
        setToast({ message: `❌ Failed to ${isEdit ? 'update' : 'add'} device`, success: false });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: `❌ Failed to ${isEdit ? 'update' : 'add'} device`, success: false });
    }
  };

  const hasUnsaved =
    name || unit || description || preview ||
    Object.values(temps).some((v) => v) ||
    Object.values(health).some((h) => Object.values(h).some((v) => v));

  const back = () => {
    if (hasUnsaved && !window.confirm('Are you sure you want to exit? Unsaved changes will be lost.')) {
      return;
    }
    navigate('/dashboard/overview');
  };

  const cardVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 },
    }),
    []
  );

  return (
    <div className="relative min-h-screen overflow-hidden p-4 sm:p-8">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10131B] via-[#0B1018] to-[#06080C]" />
        <div className="absolute -top-32 right-0 h-72 w-72 rounded-full bg-primary/40 blur-3xl opacity-60" />
        <div className="absolute bottom-0 left-[-10%] h-80 w-80 rounded-full bg-cyan-500/30 blur-3xl opacity-70" />
      </div>
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          className={`fixed right-4 top-4 z-30 flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-lg backdrop-blur-md ${
            toast.success
              ? 'border-emerald-400/40 bg-emerald-500/20 text-emerald-200'
              : 'border-rose-400/40 bg-rose-500/20 text-rose-200'
          }`}
        >
          {toast.message}
        </motion.div>
      )}
      <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <button
            onClick={back}
            className="text-xs font-medium uppercase tracking-[0.4em] text-white/50 transition hover:text-white/70"
          >
            Overview
          </button>
          <h1 className="text-3xl font-semibold text-white">
            {isEdit ? 'Edit Device Profile' : 'Add a New Device'}
          </h1>
          <p className="text-sm text-white/60">
            Provide detailed configuration to keep monitoring accurate and actionable.
          </p>
        </div>
        <div className="rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs text-white/60 backdrop-blur">
          Signed in as <span className="font-semibold text-white">{user}</span>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.4 }}
          className="w-full rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col gap-6 md:flex-row">
              <div
                className="md:w-1/3"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <label
                  htmlFor="deviceImage"
                  className="flex h-52 w-full cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-white/20 bg-black/20 p-4 text-center text-sm text-white/40 transition hover:border-white/40 hover:text-white/70"
                >
                  {preview ? (
                    <img src={preview} alt="Preview" className="h-full w-full rounded-xl object-contain" />
                  ) : (
                    <span>
                      <strong className="block text-base text-white">Upload device image</strong>
                      <span className="text-xs text-white/60">Click or drag & drop</span>
                    </span>
                  )}
                  <input id="deviceImage" type="file" accept="image/*" onChange={handleImage} className="hidden" />
                </label>
                {preview && (
                  <button
                    type="button"
                    onClick={removeImage}
                    className="mt-2 text-xs font-medium text-rose-300 underline transition hover:text-rose-200"
                  >
                    Remove image
                  </button>
                )}
              </div>
              <div className="flex-1 space-y-4">
                <input
                  type="text"
                  value={user}
                  readOnly
                  className="w-full cursor-not-allowed rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/60 opacity-70"
                />
                <input
                  type="text"
                  placeholder="Device Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 transition focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                {isEdit ? (
                  <input
                    type="text"
                    value={unit}
                    readOnly
                    className="w-full cursor-not-allowed rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/60 opacity-70"
                  />
                ) : (
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 transition focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="" className="bg-[#0B1018] text-white">
                      Select Unit
                    </option>
                    {units.map((u) => (
                      <option key={u} value={u} className="bg-[#0B1018] text-white">
                        {u}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
                  Temperature Conditions
                </h3>
                {[
                  { label: 'Avg Temp', key: 'avg', tag: 'Good', bg: 'bg-emerald-500/20 text-emerald-300' },
                  { label: 'Above Avg Temp', key: 'above', tag: 'Warning', bg: 'bg-amber-500/20 text-amber-200' },
                  { label: 'Limit', key: 'limit', tag: 'Failing', bg: 'bg-rose-500/20 text-rose-200' },
                ].map((row) => (
                  <div key={row.key} className="flex items-center gap-3 rounded-2xl border border-white/5 bg-black/20 px-4 py-3">
                    <label className="w-24 text-xs uppercase tracking-wide text-white/60">
                      {row.label}
                    </label>
                    <input
                      type="text"
                      placeholder="°C"
                      value={temps[row.key]}
                      onChange={(e) => setTemps({ ...temps, [row.key]: e.target.value })}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-right text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none focus:ring-1 focus:ring-primary/40"
                    />
                    <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide ${row.bg}`}>
                      {row.tag}
                    </span>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
                  Health Thresholds
                </h3>
                {[
                  { key: 'avg', label: 'Avg', tag: 'Good', bg: 'bg-emerald-500/20 text-emerald-300' },
                  { key: 'above', label: 'Warning', tag: 'Warning', bg: 'bg-amber-500/20 text-amber-200' },
                  { key: 'limit', label: 'Failing', tag: 'Failing', bg: 'bg-rose-500/20 text-rose-200' },
                ].map((row) => (
                  <div key={row.key} className="flex items-center gap-3 rounded-2xl border border-white/5 bg-black/20 px-4 py-3">
                    <label className="w-20 text-xs uppercase tracking-wide text-white/60">{row.label}</label>
                    {['x', 'y', 'z'].map((axis) => (
                      <input
                        key={axis}
                        type="text"
                        placeholder={`${axis.toUpperCase()} mm/s`}
                        value={health[row.key][axis]}
                        onChange={(e) => handleHealthChange(row.key, axis, e.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-right text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none focus:ring-1 focus:ring-primary/40"
                      />
                    ))}
                    <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide ${row.bg}`}>
                      {row.tag}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <textarea
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 transition focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
              rows="4"
            />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-white/40">
                Ensure all thresholds are configured according to your maintenance policy.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {isEdit && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="inline-flex items-center justify-center rounded-full border border-rose-500/30 bg-rose-500/10 px-5 py-2 text-sm font-semibold text-rose-200 transition hover:border-rose-400/60 hover:bg-rose-500/20"
                  >
                    Remove Profile
                  </button>
                )}
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-primary via-rose-500 to-orange-400 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition-all hover:shadow-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {isEdit ? 'Save Changes' : 'Add Device'}
                </button>
              </div>
            </div>
          </form>
        </motion.div>

        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.4, delay: 0.15 }}
          className="w-full rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl"
        >
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/10 p-2">
              <img src={InfoIcon} alt="Reference" className="h-full w-full" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-white">ISO 10816 – Vibration Severity Reference</h2>
              <p className="mt-2 text-xs leading-relaxed text-white/70">
                Keep this quick reference handy while configuring devices. Severity bands map vibration velocity to machine classes so you can align your limits with ISO 10816 guidance.
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-4 text-xs text-white/70 md:grid-cols-2">
            <p>
              <strong className="text-white">Class I:</strong> Separate driver and driven units up to approximately 15kW (≈20hp).
            </p>
            <p>
              <strong className="text-white">Class II:</strong> Motors 15–75kW (20–100hp) without special foundations or machines up to 300kW (400hp) on rigid bases.
            </p>
            <p>
              <strong className="text-white">Class III:</strong> Large prime movers or heavy machines with rigid assemblies on solid foundations.
            </p>
            <p>
              <strong className="text-white">Class IV:</strong> Large machines mounted on relatively soft foundations.
            </p>
          </div>
          <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-black/20">
            <table className="min-w-full text-[11px] text-white/80">
              <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-white/60">
                <tr>
                  <th colSpan="2" className="border border-white/5 px-3 py-2">Velocity Severity</th>
                  <th colSpan="4" className="border border-white/5 px-3 py-2">Velocity Range Limits &amp; Machine Classes</th>
                </tr>
                <tr>
                  <th className="border border-white/5 px-2 py-2">mm/s RMS</th>
                  <th className="border border-white/5 px-2 py-2">in/s Peak</th>
                  <th className="border border-white/5 px-2 py-2">Class I</th>
                  <th className="border border-white/5 px-2 py-2">Class II</th>
                  <th className="border border-white/5 px-2 py-2">Class III</th>
                  <th className="border border-white/5 px-2 py-2">Class IV</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { mm: '0.28', in: '0.02', cells: ['', '', '', ''] },
                  { mm: '0.45', in: '0.03', cells: ['Good', '', '', ''] },
                  { mm: '0.71', in: '0.04', cells: ['Good', 'Good', 'Good', 'Good'] },
                  { mm: '1.12', in: '0.06', cells: ['Satisfactory', '', '', ''] },
                  { mm: '1.80', in: '0.10', cells: ['', 'Satisfactory', '', ''] },
                  { mm: '2.80', in: '0.16', cells: ['Unsatisfactory', '', 'Satisfactory', ''] },
                  { mm: '4.50', in: '0.25', cells: ['', 'Unsatisfactory', '', 'Satisfactory'] },
                  { mm: '7.10', in: '0.40', cells: ['Unacceptable', '', 'Unsatisfactory', ''] },
                  { mm: '11.20', in: '0.62', cells: ['', 'Unacceptable', '', 'Unsatisfactory'] },
                  { mm: '18.00', in: '1.00', cells: ['', '', 'Unacceptable', ''] },
                  { mm: '28.00', in: '1.56', cells: ['', '', '', 'Unacceptable'] },
                  { mm: '45.00', in: '2.51', cells: ['', '', '', ''] },
                ].map((row, index) => (
                  <tr key={`${row.mm}-${index}`} className="odd:bg-white/5">
                    <td className="border border-white/5 px-3 py-2 text-center">{row.mm}</td>
                    <td className="border border-white/5 px-3 py-2 text-center">{row.in}</td>
                    {row.cells.map((cell, cellIdx) => (
                      <td key={cellIdx} className="border border-white/5 px-3 py-2 text-center">
                        {cell && (
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-[10px] font-semibold ${
                              cell === 'Good'
                                ? 'bg-emerald-500/20 text-emerald-200'
                                : cell === 'Satisfactory'
                                ? 'bg-cyan-500/20 text-cyan-200'
                                : cell === 'Unsatisfactory'
                                ? 'bg-amber-500/20 text-amber-200'
                                : 'bg-rose-500/20 text-rose-200'
                            }`}
                          >
                            {cell}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default AddNewDevice;
